import React, { useState, useEffect } from 'react';
import CopyToClipboard from '../../copyToClipboard';
import { Mail, PhoneIcon, ChevronRight } from 'assets/icons';
import AdminCollapseFile from '../../collapse/AdminCollapseFile';
import Tooltip from '../../tooltips/Tooltips';

const IdentityValidationMobileCard = ({ 
  tempWorker, 
  nbFiles, 
  nbFilesTotal, 
  onOpenTempWorker,
  isSelected 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Document states
  const [isCNIFiles, setIsCNIFiles] = useState(false);
  const [isPassportFiles, setIsPassportFiles] = useState(false);
  const [isSelfieFiles, setIsSelfieFiles] = useState(false);
  const [isVisaFiles, setIsVisaFiles] = useState(false);
  const [isVitalCardFiles, setIsVitalCardFiles] = useState(false);
  const [isProofOfAddressFiles, setIsProofOfAddressFiles] = useState(false);
  const [isAccommodationFiles, setIsAccommodationFiles] = useState(false);
  const [isHostIdentityFiles, setIsHostIdentityFiles] = useState(false);

  const [statusCNIFiles, setStatusCNIFiles] = useState(1);
  const [statusPassportFiles, setStatusPassportFiles] = useState(1);
  const [statusSelfieFiles, setStatusSelfieFiles] = useState(1);
  const [statusVisaFiles, setStatusVisaFiles] = useState(1);
  const [statusVitalCardFiles, setStatusVitalCardFiles] = useState(1);
  const [statusProofOfAddressFiles, setStatusProofOfAddressFiles] = useState(1);
  const [statusAccommodationFiles, setStatusAccommodationFiles] = useState(1);
  const [statusHostIdentityFiles, setStatusHostIdentityFiles] = useState(1);

  // Get user data from either nested user object or root level
  const user = tempWorker?.user || tempWorker;
  const firstname = user?.firstname || '';
  const lastname = user?.lastname || '';
  const email = user?.email || '';
  const phone = user?.phone || '';
  
  // Capitalize first letter of firstname and lastname
  const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  
  const capitalizedFirstname = capitalizeFirst(firstname);
  const capitalizedLastname = capitalizeFirst(lastname);
  const fullName = `${capitalizedFirstname} ${capitalizedLastname}`.trim() || 'N/A';

  const hasValidation = (v) => {
    if (v === undefined || v === null) return false;
    const num = Number(v);
    return !isNaN(num) && num >= 1;
  };

  // Initialize document states - reset all states first, then set based on current tempWorker
  useEffect(() => {
    // Reset all document states
    setIsCNIFiles(false);
    setIsPassportFiles(false);
    setIsSelfieFiles(false);
    setIsVisaFiles(false);
    setIsVitalCardFiles(false);
    setIsProofOfAddressFiles(false);
    setIsAccommodationFiles(false);
    setIsHostIdentityFiles(false);
    setStatusCNIFiles(1);
    setStatusPassportFiles(1);
    setStatusSelfieFiles(1);
    setStatusVisaFiles(1);
    setStatusVitalCardFiles(1);
    setStatusProofOfAddressFiles(1);
    setStatusAccommodationFiles(1);
    setStatusHostIdentityFiles(1);

    // Identity (CNI / Passport)
    const idValidated = tempWorker?.identity?.validated;
    if (hasValidation(idValidated)) {
      const idType = tempWorker?.identity?.type;
      const status = parseInt(idValidated);

      if (idType === 'CNI') {
        setIsCNIFiles(true);
        setStatusCNIFiles(status);
      } else {
        setIsPassportFiles(true);
        setStatusPassportFiles(status);
      }
    }

    // Selfie
    const selfieValidated = tempWorker?.selfie?.validated;
    if (hasValidation(selfieValidated)) {
      setIsSelfieFiles(true);
      setStatusSelfieFiles(parseInt(selfieValidated));
    }

    // Visa
    const visaValidated = tempWorker?.visa?.validated;
    if (hasValidation(visaValidated)) {
      setIsVisaFiles(true);
      setStatusVisaFiles(parseInt(visaValidated));
    }

    // Carte vitale
    const vitaleValidated = tempWorker?.card_vitality?.validated;
    if (hasValidation(vitaleValidated)) {
      setIsVitalCardFiles(true);
      setStatusVitalCardFiles(parseInt(vitaleValidated));
    }

    // Justificatif de domicile
    const poaValidated = tempWorker?.proof_of_address?.validated;
    if (hasValidation(poaValidated)) {
      setIsProofOfAddressFiles(true);
      setStatusProofOfAddressFiles(parseInt(poaValidated));
    }

    // Attestation d'hébergement
    const accValidated = tempWorker?.accommodation_of_address?.validated;
    if (hasValidation(accValidated)) {
      setIsAccommodationFiles(true);
      setStatusAccommodationFiles(parseInt(accValidated));
    }

    // Pièce d'identité de l'hôte
    const hostIdValidated = tempWorker?.hostIdentity?.validated;
    if (hasValidation(hostIdValidated)) {
      setIsHostIdentityFiles(true);
      setStatusHostIdentityFiles(parseInt(hostIdValidated));
    }
  }, [tempWorker]);

  return (
    <details
      className={`
        bg-white rounded-md border transition-all duration-200 group overflow-visible
        ${isSelected 
          ? 'border-yellow-500 shadow-md ring-1 ring-yellow-200' 
          : 'border-gray-200 hover:bg-gray-50 active:bg-gray-100'
        }
      `}
      onClick={() => setIsOpen(!isOpen)}
    >
      <summary className="list-none cursor-pointer">
        {/* Header */}
        <div className="p-4 pb-3">
          <div className="flex items-center justify-between gap-3">
            {/* Contenu principal : Nom + Email/Tel */}
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 flex-1 min-w-0">
              {/* Nom */}
              <Tooltip text="Voir la fiche" placement="top">
                <h3 
                  className="font-semibold text-gray-900 text-base truncate cursor-pointer hover:underline decoration-dashed transition-colors max-w-[200px] md:max-w-[250px] flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onOpenTempWorker) {
                      onOpenTempWorker(tempWorker);
                    }
                  }}
                  title={fullName}
                >
                  {fullName}
                </h3>
              </Tooltip>
              
              {/* Email et téléphone - sur la même ligne que le nom en desktop */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {email && (
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <Mail wh={16} color="#6B7280" />
                    <CopyToClipboard text={email}>
                      <span className="text-gray-600 truncate max-w-[180px] md:max-w-[250px]">{email}</span>
                    </CopyToClipboard>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <PhoneIcon wh={16} color="#6B7280" />
                    <CopyToClipboard text={phone}>
                      <span className="text-gray-600">{phone}</span>
                    </CopyToClipboard>
                  </div>
                )}
              </div>
            </div>
            
            {/* Badge documents + chevron */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="inline-flex items-center justify-center rounded-md px-2 py-1 text-sm font-medium ring-1 ring-inset bg-gray-50 text-gray-600 ring-gray-500/10 tabular-nums min-w-[3rem]">
                {nbFiles}/{nbFilesTotal}
              </span>
              <div className="group-open:rotate-90 transition-transform flex-shrink-0">
                <ChevronRight wh={20} color="#4B5563" />
              </div>
            </div>
          </div>
        </div>
      </summary>

      {/* Accordion content - Documents */}
      <div className="px-4 pb-4 border-t border-gray-100 pt-3">
        {isCNIFiles && (
          <AdminCollapseFile
            title={"Carte d'identité"}
            dateAdd={tempWorker.identity?.updated}
            status={
              statusCNIFiles === 1
                ? 4
                : statusCNIFiles === 2
                ? 5
                : statusCNIFiles
            }
            type="identity"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isPassportFiles && (
          <AdminCollapseFile
            title={'Passeport'}
            dateAdd={tempWorker.identity?.updated}
            status={
              statusPassportFiles === 1
                ? 4
                : statusPassportFiles === 2
                ? 5
                : statusPassportFiles
            }
            type="identity"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isSelfieFiles && (
          <AdminCollapseFile
            title={'Selfie'}
            dateAdd={tempWorker.selfie?.updated}
            status={
              statusSelfieFiles === 1
                ? 4
                : statusSelfieFiles === 2
                ? 5
                : statusSelfieFiles
            }
            type="selfie"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isVisaFiles && (
          <AdminCollapseFile
            title={'Visa'}
            dateAdd={tempWorker.visa?.updated}
            status={
              statusVisaFiles === 1
                ? 4
                : statusVisaFiles === 2
                ? 5
                : statusVisaFiles
            }
            type="visa"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isVitalCardFiles && (
          <AdminCollapseFile
            title={'Carte vitale'}
            dateAdd={tempWorker.card_vitality?.updated}
            status={
              statusVitalCardFiles === 1
                ? 4
                : statusVitalCardFiles === 2
                ? 5
                : statusVitalCardFiles
            }
            type="card_vitality"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isProofOfAddressFiles && (
          <AdminCollapseFile
            title={'Justificatif de domicile'}
            dateAdd={tempWorker.proof_of_address?.updated}
            status={
              statusProofOfAddressFiles === 1
                ? 4
                : statusProofOfAddressFiles === 2
                ? 5
                : statusProofOfAddressFiles
            }
            type="proof_of_address"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isAccommodationFiles && (
          <AdminCollapseFile
            title={"Attestation d'hébergement"}
            dateAdd={tempWorker.accommodation_of_address?.updated}
            status={
              statusAccommodationFiles === 1
                ? 4
                : statusAccommodationFiles === 2
                ? 5
                : statusAccommodationFiles
            }
            type="accommodation_of_address"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}

        {isHostIdentityFiles && (
          <AdminCollapseFile
            title={"Pièce d'identité de l'hôte"}
            dateAdd={tempWorker.hostIdentity?.updated}
            status={
              statusHostIdentityFiles === 1
                ? 4
                : statusHostIdentityFiles === 2
                ? 5
                : statusHostIdentityFiles
            }
            type="hostIdentity"
            _id={tempWorker._id}
            id_user={tempWorker.id_user}
          />
        )}
      </div>
    </details>
  );
};

export default IdentityValidationMobileCard;
